import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Pagination, Row, Select, Space, Table, Typography, message } from "antd";
import type { PaginationProps, TableProps } from "antd";
import { BsPencilSquare } from "react-icons/bs";
import { usePagination } from "src/hook/usePagination";
import { useDidMount } from "src/hook/useDidMount";
import { useShallow } from "zustand/react/shallow";
import { CommonSuccess } from "src/entity/common/menus-enum.enum";
import { profileStore } from "src/stores/profile-store";

import { utilsStore } from "src/stores/utils-store";
import { isNull, omitBy } from "lodash";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { canEditTable, canViewTable } from "../../PermissionCheck/PermissionCheck";
import { formatNumber } from "src/utils/process-number";
import { convertEmissionWord } from "src/entity/common/global.entity";
import { CalculationMethodOthersEnum } from "src/entity/common/common.constant";
import { optionsSelectYears } from "src/utils/array";
import { othersStore } from "src/stores/others-store";
import {
  FillEmissionObject,
  FilterOthersFillEmission,
  OthersEmission,
  ResponseOthersFillEmission,
  UpdateOthersFillEmission,
} from "src/entity/others/fill-emission";
import FillEmissionModal from "./FillEmissionModal";

const { Text } = Typography;
const calculationMethodFillEmission = CalculationMethodOthersEnum.FILL_EMISSION;
interface FillEmissionTableProps {}

const FillEmissionTable: React.FC<FillEmissionTableProps> = ({}) => {
  const currentYear = new Date().getFullYear();
  const [messageApi, contextHolder] = message.useMessage();

  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());
  const [visibleModal, setVisibleModal] = useState(false);
  const [isEdit, setIsEdit] = useState<ResponseOthersFillEmission | null>(null);
  const [tableKey, setTableKey] = useState(0);

  const { permissions } = useNavigationMenu();

  const profile = profileStore(useShallow((state) => state.profile));
  const getAreaInCompany = utilsStore(useShallow((state) => state.getAreaInCompany));
  const dataAreaInCompany = utilsStore(useShallow((state) => state.dataAreaInCompany));

  // Group 1. masterdata, utils data
  const getAllDistinctColFillEmission = othersStore(useShallow((state) => state.getAllDistinctColFillEmission));
  const dataFilterColFillEmission = othersStore(useShallow((state) => state.dataFilterColFillEmission));

  // Group 2. Main Service, main data
  const getOthersFillEmission = othersStore(useShallow((state) => state.getOthersFillEmission));
  const dataFillEmission = othersStore(useShallow((state) => state.dataFillEmission));
  const totalDocs = dataFillEmission.total;

  const updateOthersFillEmission = othersStore(useShallow((state) => state.updateOthersFillEmission));
  const loadingManageOthers = othersStore(useShallow((state) => state.loading.update));
  //   const calculateReportVocWasteWater = reportWasteWaterStore(useShallow((state) => state.calculateReportVocWasteWater));

  const clearError = othersStore(useShallow((state) => state.clearError));
  const errorFillEmission = othersStore(useShallow((state) => state.error));

  const getMasterLabel = (type: string, text: string) => {
    if (!text || typeof text != "string") return "";
    if (type === "area" && dataAreaInCompany) {
      const optValue = dataAreaInCompany.find((item) => item._id === text);
      return optValue?.areaName;
    } else return text;
  };

  const onClickEdit = (item: ResponseOthersFillEmission) => {
    setIsEdit(item);
    setVisibleModal(true);
  };

  const onUpdateKeyValue = (key: keyof OthersEmission, value: string | number, month: string) => {
    const tmpIsEdit = { ...isEdit } as ResponseOthersFillEmission;
    tmpIsEdit.cloneEmission[key].fillEmission[month as keyof FillEmissionObject] = value;
    setIsEdit(tmpIsEdit);
  };

  const {
    onChange,
    filter,
    page,
    setPage,
    loading,
    limit: pageSize,
    data: sourceData,
  } = usePagination<ResponseOthersFillEmission, FilterOthersFillEmission>({
    action: getOthersFillEmission,
    remoteData: dataFillEmission.data,
    initialFilter: {
      year: [selectedYear],
      calculationMethod: calculationMethodFillEmission,
    },
  });

  const currentFilterExcludePage = useMemo(() => {
    const result = omitBy(filter, (value, key) => key === "page" || key === "year" || key === "limit" || isNull(value));
    return Object.keys(result).length;
  }, [filter]);

  const areaFilter = useMemo(() => {
    if ((dataFillEmission.data?.length === 0 || dataFilterColFillEmission.area.length === 0) && currentFilterExcludePage == 0) return [];
    return dataFilterColFillEmission.area.map((item) => {
      const findLabel = dataAreaInCompany.find((e) => e._id === item);
      return {
        text: findLabel?.areaName,
        value: item,
      };
    });
  }, [dataFilterColFillEmission.area, dataFillEmission.data, dataAreaInCompany]);
  const sourceNameFilter = useMemo(() => {
    if (dataFilterColFillEmission.sourceName?.length === 0 && currentFilterExcludePage == 0) return [];
    return dataFilterColFillEmission.sourceName.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [dataFilterColFillEmission.sourceName, dataFillEmission.data]);

  const emissionFilter = useMemo(() => {
    if ((dataFillEmission.data?.length === 0 || dataFilterColFillEmission.emission.length === 0) && currentFilterExcludePage == 0)
      return [];
    return dataFilterColFillEmission.emission.map((item) => {
      return {
        text: convertEmissionWord(item.toUpperCase()),
        value: item,
      };
    });
  }, [dataFilterColFillEmission.emission, dataFillEmission.data]);

  const handleTableChange: TableProps<ResponseOthersFillEmission>["onChange"] = (_, filters) => {
    // onChange(filters);

    onChange({
      ...filters,
      calculationMethod: calculationMethodFillEmission,
    });
  };

  const getIndex = (index: number) => (page - 1) * pageSize + index;
  const columns: TableProps<ResponseOthersFillEmission>["columns"] = [
    {
      title: "No",
      dataIndex: "no",
      key: "no",
      render: (text, record, index) => {
        const obj = {
          children: <Text>{getIndex(+text)}</Text>,
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1]._id !== record._id) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item._id === record._id).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
      align: "left",
    },
    {
      title: "Source Name",
      dataIndex: "sourceName",
      key: "sourceName",
      render: (text, record, index) => {
        const obj = {
          children: <Text>{text}</Text>,
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1]._id !== record._id) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item._id === record._id).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
      filters: !canViewTable(permissions) ? [] : sourceNameFilter,
      align: "left",
    },
    {
      title: "Area",
      dataIndex: "area",
      key: "area",
      render: (text, record, index) => {
        const obj = {
          children: <Text>{getMasterLabel("area", text)}</Text>,
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1]._id !== record._id) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item._id === record._id).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
      filters: !canViewTable(permissions) ? [] : areaFilter,
      align: "left",
    },
    {
      title: "Emission",
      key: "emission",
      render: (_, record) => <Text>{convertEmissionWord(record.activeEmission)}</Text>,
      align: "left",
      filters: !canViewTable(permissions) ? [] : emissionFilter,
    },
    {
      title: "Unit",
      key: "unit",
      render: (_, record) => <Text>{record.unit}</Text>,
      align: "left",
    },
    {
      title: "Jan",
      key: "jan",
      render: (_, record) => <Text>{formatNumber(record["jan"] as number, 2)}</Text>,
      align: "left",
    },
    {
      title: "Feb",
      key: "feb",
      render: (_, record) => <Text>{formatNumber(record["feb"] as number, 2)}</Text>,
      align: "left",
    },
    {
      title: "Mar",
      key: "mar",
      render: (_, record) => <Text>{formatNumber(record["mar"] as number, 2)}</Text>,
      align: "left",
    },
    {
      title: "Apr",
      key: "apr",
      render: (_, record) => <Text>{formatNumber(record["apr"] as number, 2)}</Text>,
      align: "left",
    },
    {
      title: "May",
      key: "may",
      render: (_, record) => <Text>{formatNumber(record["may"] as number, 2)}</Text>,
      align: "left",
    },
    {
      title: "Jun",
      key: "jun",
      render: (_, record) => <Text>{formatNumber(record["jun"] as number, 2)}</Text>,
      align: "left",
    },
    {
      title: "Jul",
      key: "jul",
      render: (_, record) => <Text>{formatNumber(record["jul"] as number, 2)}</Text>,
      align: "left",
    },
    {
      title: "Aug",
      key: "aug",
      render: (_, record) => <Text>{formatNumber(record["aug"] as number, 2)}</Text>,
      align: "left",
    },
    {
      title: "Sep",
      key: "sep",
      render: (_, record) => <Text>{formatNumber(record["sep"] as number, 2)}</Text>,
      align: "left",
    },
    {
      title: "Oct",
      key: "oct",
      render: (_, record) => <Text>{formatNumber(record["oct"] as number, 2)}</Text>,
      align: "left",
    },
    {
      title: "Nov",
      key: "nov",
      render: (_, record) => <Text>{formatNumber(record["nov"] as number, 2)}</Text>,
      align: "left",
    },
    {
      title: "Dec",
      key: "dec",
      render: (_, record) => <Text>{formatNumber(record["dec"] as number, 2)}</Text>,
      align: "left",
    },
    {
      title: "Action",
      key: "action",
      render: (text, record, index) => {
        const obj = {
          children: (
            <Space size="middle" direction={"horizontal"}>
              <Button
                className="border-transparent bg-transparent"
                size={"small"}
                onClick={() => onClickEdit(record)}
                disabled={!canEditTable(permissions)}
              >
                <BsPencilSquare size={18} />
              </Button>
            </Space>
          ),
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1].no !== record.no) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item.no === record.no).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
      align: "left",
    },
  ];

  const getColumnFilter = () => {
    getAllDistinctColFillEmission(selectedYear, calculationMethodFillEmission);
  };

  const initialRequestDidMount = (isDidMount?: boolean) => {
    getAreaInCompany(profile?.activeCompany._id as string);
    getColumnFilter();
    getOthersFillEmission(
      isDidMount === true
        ? { ...filter, page, limit: pageSize }
        : { page, limit: pageSize, year: [selectedYear], calculationMethod: CalculationMethodOthersEnum.FILL_EMISSION },
    );
  };

  useDidMount(() => {
    initialRequestDidMount(true);
  });

  useEffect(() => {
    if (profile?.activeCompany) {
      setPage(1);
      setTableKey(tableKey + 1);
    }
  }, [profile?.activeCompany, selectedYear]);

  useEffect(() => {
    if (tableKey > 0) {
      initialRequestDidMount(false);
    }
  }, [tableKey]);

  const onChangePage = (val: number) => {
    setPage(val);
  };

  const paginationProps: PaginationProps = {
    size: "small",
    pageSize: pageSize,
    current: page,
    total: totalDocs || 0,
    defaultCurrent: 1,
    onChange: onChangePage,
    showSizeChanger: false,
  };

  const closeModal = () => {
    setVisibleModal(false);
    clearError();
    setIsEdit(null);
  };

  const submitFillEmission = async () => {
    // console.log("Process final data : ", isEdit);
    const processData = { emission: isEdit?.cloneEmission } as UpdateOthersFillEmission;
    updateOthersFillEmission(isEdit?._id as string, processData).then(onFinishAction);
  };

  const handleChangeYear = (year: string) => {
    // console.log("Year : ", year);
    onChange({ ...filter, year: [year], calculationMethod: CalculationMethodOthersEnum.FILL_EMISSION });
    setSelectedYear(year);
  };

  const onFinishAction = (response?: unknown) => {
    // console.log("response : ", response);
    if (typeof response === "string") {
      return;
    } else {
      messageApi.open({
        type: "success",
        content: CommonSuccess.SAVED_SUCCESSFULL,
      });
      //   calculateReportVocWasteWater(isEdit?._id as string);
      getColumnFilter();
      getOthersFillEmission({
        ...filter,
        page,
        limit: pageSize,
        year: [selectedYear],
        calculationMethod: CalculationMethodOthersEnum.FILL_EMISSION,
      });
      closeModal();
    }
  };

  return (
    <Row className="w-full" id={"wrap-fill-emission-table"}>
      {contextHolder}
      <Row className="w-full" justify={"space-between"}>
        <Select value={selectedYear} style={{ width: 120 }} onChange={handleChangeYear} options={optionsSelectYears()} size="middle" />
      </Row>
      <Table
        columns={columns}
        dataSource={!canViewTable(permissions) ? [] : (sourceData as ResponseOthersFillEmission[])}
        className="w-full mt-4"
        loading={loading}
        pagination={false}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
        rowKey={(rec, index) => `${rec._id}-${index}-${rec.no}`}
      />

      <Row className="w-full mt-4" justify={"end"}>
        <Pagination {...paginationProps} />
      </Row>

      <FillEmissionModal
        isEdit={isEdit}
        getMasterLabel={getMasterLabel}
        submitFillEmission={submitFillEmission}
        loadingManageFillEmission={loadingManageOthers}
        visibleModal={visibleModal}
        closeModal={closeModal}
        errorFillEmission={errorFillEmission}
        onUpdateKeyValue={onUpdateKeyValue}
      />
    </Row>
  );
};

export default FillEmissionTable;
